import { Injectable, OnDestroy } from '@angular/core';
import { from, Observable, of, BehaviorSubject, ReplaySubject } from 'rxjs';
import { take, switchMap, filter } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { IQuestion, IQuestionResponse } from '../../../../interfaces/question.interface';
import { CheckListsApiService } from '../../../../../../services/check-lists-api.service';
import { IChecklist } from '../../../../interfaces/checklist.interface';
import { IQuestionCategory } from '../../../../interfaces/question-category.interface';
import { ConfirmModalComponent } from '../../../../../../shared/modals/confirm-modal/confirm-modal.component';
import { ISaveChecklist } from '../../../../interfaces/save-checklist.interface';
import { QuestionDialogComponent } from '../question-dialog/question-dialog.component';
import { IDeleteQuestionError } from '../../../../interfaces/delete-questions-response.interface';
import { ITicketType } from '../../../../interfaces/ticket-type.interface';
import { ICreateChecklistParams } from '../../../../interfaces/create-checklist-params.interface';
import { IGlobalProblemSubtype } from '../../../../interfaces/global-problem-subtype.interface';

@Injectable()
export class CheckListsService implements OnDestroy {
  questionsLoading$ = new BehaviorSubject<boolean>(false);
  checklistLoading$ = new BehaviorSubject<boolean>(false);
  questions$ = new ReplaySubject<IQuestion[]>(1);
  checklists$ = new BehaviorSubject<IChecklist[]>([]);
  checklist$ = new ReplaySubject<IChecklist>(1);
  categories$ = new ReplaySubject<IQuestionCategory[]>(1);
  hdTicketTypes$ = new ReplaySubject<ITicketType[]>(1);
  gpSubtypes$ = new BehaviorSubject<IGlobalProblemSubtype[]>([]);

  totalItem = 0;
  constructor(
    private checkListsApiService: CheckListsApiService,
    private modalService: NgbModal
  ) {}

  loadQuestions(params = { category: '', search: '', limit: 100, offset: 0 }) {
    this.questionsLoading$.next(true);
    this.questions$.next([]);
    this.totalItem = 0;
    this.checkListsApiService.getQuestions(params).subscribe(
      (questions: IQuestionResponse) => {
        this.totalItem = questions.count;
        this.questions$.next(questions.results);
      },
      null,
      () => {
        this.questionsLoading$.next(false);
      }
    );
  }

  loadQuestionCategories(): void {
    this.checkListsApiService.getQuestionCategories().subscribe((categories: IQuestionCategory[]) => {
      this.categories$.next(categories);
    });
  }

  loadChecklists(): void {
    this.checkListsApiService.getChecklists<IChecklist[]>().subscribe((checklists) => {
      this.checklists$.next(checklists);
    });
  }

  loadHDTicketTypes(): void {
    this.checkListsApiService.getHDTicketTypes<ITicketType[]>().subscribe((hdTicketTypes: ITicketType[]) => {
      this.hdTicketTypes$.next(hdTicketTypes);
    });
  }

  loadGPSubtypes(): void {
    this.checkListsApiService.getGPSubtypes().subscribe((gpSubtypes: IGlobalProblemSubtype[]) => {
      this.gpSubtypes$.next(gpSubtypes);
    });
  }

  loadChecklist(ticketType: string): void {
    this.checklistLoading$.next(true);
    this.checkListsApiService.getChecklists<IChecklist>(ticketType).subscribe(
      (checklist) => {
        this.checklist$.next(checklist);
      },
      () => {},
      () => {
        this.checklistLoading$.next(false);
      }
    );
  }

  createQuestion(data: Partial<IQuestion>) {
    return this.checkListsApiService.createQuestion(data);
  }

  createCategory(categoryName: string): Observable<IQuestionCategory> {
    return this.checkListsApiService.createCategory(categoryName);
  }

  tryToDeleteQuestions(questionIds: number[]) {
    return this.checkListsApiService.deleteQuestions(questionIds);
  }

  saveChecklistQuestions(ticketType: string, saveChecklists: ISaveChecklist[]): Observable<IChecklist> {
    return this.checkListsApiService.saveChecklistQuestions(ticketType, saveChecklists);
  }

  saveChecklist(ticketType: string, checklist: Partial<IChecklist>): Observable<IChecklist> {
    return this.checkListsApiService.saveChecklist(ticketType, checklist);
  }

  createChecklist(params: ICreateChecklistParams): Observable<IChecklist> {
    return this.checkListsApiService.createChecklist(params);
  }

  showQuestionDialog(): Observable<Partial<IQuestion> & { createCategory: string }> {
    return this.categories$.pipe(take(1)).pipe(
      switchMap((categories: IQuestionCategory[]) => {
        const questionDialog = this.modalService.open(QuestionDialogComponent, {
          ariaLabelledBy: 'modal-basic-title',
          windowClass: 'modal-wrapper modal-question-dialog',
        });

        questionDialog.componentInstance.categories = categories;
        return from(questionDialog.result).pipe(take(1), filter(value => Boolean(value)));
      })
    );
  }

  showDeleteDialog(description: string, successButtonName = 'Удалить'): Observable<boolean> {
    const confirmDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmDialog.componentInstance.title = 'Удаление вопросов';
    confirmDialog.componentInstance.description = description;
    confirmDialog.componentInstance.successButtonName = successButtonName;
    confirmDialog.componentInstance.abortButtonName = 'Отмена';

    return from(confirmDialog.result).pipe(catchError(() => of(false)));
  }

  showErrorDialog(message: string): Observable<boolean> {
    const errorDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    errorDialog.componentInstance.title = 'Извините';
    errorDialog.componentInstance.errorMessage = message;
    errorDialog.componentInstance.successButtonName = 'Ок';

    return from(errorDialog.result);
  }

  showSuccessDialog(message?: string): Observable<void> {
    const errorDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    errorDialog.componentInstance.title = 'Успех';
    errorDialog.componentInstance.description = message;
    errorDialog.componentInstance.successButtonName = 'Ок';

    return from(errorDialog.result);
  }

  showDeleteDialogSuccess(errors: IDeleteQuestionError[]): Observable<void> {
    const dialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper modal-wrapper--w-520',
      backdrop: 'static',
    });

    const message = errors.length
      ? `<ul class="unstyled-list">${errors.map((error) => `<li>Вопрос № ${error.q_id} - ${error.message} <br /></li>`).join('')}</ul>`
      : `Вопросы успешно удалены`;

    dialog.componentInstance.title = 'Удаление вопросов';
    dialog.componentInstance.successButtonName = 'Ок';

    if (errors.length) {
      dialog.componentInstance.errorMessage = message;
    } else {
      dialog.componentInstance.description = message;
    }

    return from(dialog.result);
  }

  showSaveDialog(checklistName: string, hasChanges: boolean, hasEnablerChanges: boolean): Observable<void> {
    const confirmDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmDialog.componentInstance.title = 'Сохранение подобранных вопросов';
    confirmDialog.componentInstance.description = `
      ${hasChanges ? `Вопросы будут сохранены в чек-лист “${checklistName}”` : ''}
      ${hasEnablerChanges ? 'Состояние чек-листа будет изменено' : ''}
    `;
    confirmDialog.componentInstance.successButtonName = 'Сохранить';
    confirmDialog.componentInstance.abortButtonName = 'Отмена';

    return from(confirmDialog.result);
  }

  showSavedDialog(checklistName: string): Observable<void> {
    const confirmDialog = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    confirmDialog.componentInstance.title = 'Сохранение чек-листа';
    confirmDialog.componentInstance.description = `Чек-лист “${checklistName}” успешно сохранен`;
    confirmDialog.componentInstance.successButtonName = 'Ок';

    return from(confirmDialog.result);
  }

  ngOnDestroy() {
    this.checklist$.complete();
    this.checklists$.complete();
    this.questions$.complete();
  }
}
